import WarpElement from "@warp-ds/elements-core";
import { i18n } from "@lingui/core";
import { css, html } from "lit";
import type { NewCarMakeListItem } from "../../models/newCarSections.js";
import { trackClickEvent } from "../tracking.js";
import { activateI18n } from "../../utils/i18n.js";

/**
 * "Digitala showrooms för nya bilar" — the brand showroom grid of the Blocket New Car tab.
 * Data is opaque pass-through from mb-new-car-api (Blocket-only, Swedish). Each card is a link
 * to the make's showroom page (href computed server-side in renderPage). Mirrors the
 * mb-new-car-frontend MakeCard stretched-anchor pattern: an empty link covers the card and
 * takes its accessible name from the heading via `aria-owns`, so the card is one link with no
 * nested interactive content; the images are decorative (the brand is conveyed by the heading).
 */
class NewCarMakeList extends WarpElement {
    declare header: string;
    declare description: string;
    declare items: NewCarMakeListItem[];

    static properties = {
        header: { type: String },
        description: { type: String },
        items: { type: Array },
    };

    static styles = [
        WarpElement.styles,
        css`
            :host {
                display: block;
            }
            @css-placeholder-Yl7K8w;
        `,
    ];

    constructor() {
        super();
        this.header = "";
        this.description = "";
        this.items = [];
    }

    connectedCallback() {
        // Re-activate i18n on the client (from <html lang>) so render()'s i18n.t() resolves after
        // hydration; SSR already has the request locale active. Same as the sibling components.
        activateI18n();
        super.connectedCallback();
    }

    render() {
        return html`<div class="grid gap-16 max-w-full">
            <div class="grid gap-4">
                <h3 class="t3 mb-0">${this.header}</h3>
                ${this.description ? html`<p class="s-text-subtle mb-0">${this.description}</p>` : ""}
            </div>
            <div class="grid gap-16 grid-cols-1 md:grid-cols-3">
                ${this.items.map((item) => {
                    // Built on Warp <w-card>, same scaffold as new-car-featured-models:
                    // `relative grid grid-rows-1 h-full` relies on Warp's surface chrome and fills the
                    // grid-stretched cell — see new-car-featured-models for the grid-rows-1 / SSR
                    // duplicate-surface rationale. Two make-card-specific points:
                    // - The thumbnail is flush to the card edge (the content row has no left padding), so
                    //   corner clipping matters here, unlike the model card whose image is inset by p-16.
                    //   No `overflow-hidden` is needed on the host: Warp's surface already clips its slotted
                    //   content (overflow:hidden + rounded), which rounds the flush thumbnail's corners.
                    // - The heading link carries Warp's `data-card-action`, whose `::after` stretches over the
                    //   card. It comes after the `relative` image block in DOM, so it paints on top.
                    return html`<w-card class="relative grid grid-rows-1 h-full">
                        <div class="flex items-center gap-24 pr-16 h-full">
                            <div class="relative w-[112] h-[112] shrink-0">
                                <img class="w-full h-full object-cover" src="${item.image}" alt="" loading="lazy" />
                                <div class="absolute inset-0" style="background-color: rgba(0, 0, 0, 0.3)"></div>
                                <img class="absolute left-12 top-12 w-32 h-32 object-contain" src="${item.logoUrl}" alt="" loading="lazy" />
                            </div>
                            <div class="flex flex-col gap-4 min-w-0">
                                <h4 class="font-bold s-text text-body">
                                    <a
                                        data-card-action
                                        href="${item.href}"
                                        @click="${() => item.trackingEvent && trackClickEvent(item.trackingEvent)}"
                                    >
                                        ${item.name}</a
                                    >
                                </h4>
                                <span class="text-s s-text-subtle"
                                    >${i18n.t({
                                        id: "newcar.makecard.model-count",
                                        message: "{count, plural, one {# model} other {# models}}",
                                        values: { count: item.modelCount },
                                    })}</span
                                >
                            </div>
                        </div>
                    </w-card>`;
                })}
            </div>
        </div>`;
    }
}

customElements.define("new-car-make-list", NewCarMakeList as unknown as CustomElementConstructor);
